import { SidebarData } from "../core/data/json/sidebarData"

/**
 * Manually appends query parameters to a URL by string concatenation.
 * @param url - The base URL.
 * @param params - An object containing key-value pairs for query parameters.
 * @returns The resulting URL with query parameters appended.
 */
const appendQueryParams = (
  url: string,
  params: Record<string, string | number | boolean | undefined | null>
): string => {
  const queryString = Object.entries(params)
    .filter(
      ([_, value]) => value !== undefined && value !== null && value !== ""
    )
    .map(
      ([key, value]) =>
        `${encodeURIComponent(key)}=${encodeURIComponent(String(value))}`
    )
    .join("&")

  const separator = url.includes("?") ? "&" : "?"
  return queryString ? `${url}${separator}${queryString}` : url
}

// Function to filter sidebar data based on permissions
export const filterSidebarByPermissions = (
  permissions?: string[],
  isSuperAdmin = false,
  isOwner = false,
  isTeacherRole = false,
  hasFy1Assignment = true
) => {
  // If permissions array is empty, return all routes
  // if (!permissions || permissions.length === 0) {
  //   return SidebarData
  // }

  // Helper function to recursively filter sidebar items
  const filterItems = (items: any) => {
    return items
      .map((item: any) => {
        // Admin/staff-only group (e.g. RMS -> Operations/Certificates/
        // Configuration) -- hidden for role "teacher" regardless of the
        // permission fail-open below, matching RMS's own minimal teacher nav.
        if (item.hideForTeacher && isTeacherRole) {
          return null
        }

        // FY1 Checklist (Eon-only) -- was always shown with a graceful
        // "no assignment" empty state like Mark Entry, but unlike Mark Entry
        // it's relevant to only a handful of teachers, so every other
        // teacher saw a dead menu item. Reported 2026-11-02: hide it for a
        // teacher unless they're actually the class teacher of an FY1
        // section. Non-teacher roles (admin browsing the sidebar) are
        // unaffected.
        if (item.requiresFy1Assignment && isTeacherRole && !hasFy1Assignment) {
          return null
        }

        // If item has submenuItems, filter them first
        if (item.submenuItems && Array.isArray(item.submenuItems)) {
          const filteredSubmenuItems = filterItems(item.submenuItems)

          // If all submenu items are filtered out, remove the parent item too
          if (filteredSubmenuItems.length === 0) {
            return null
          }

          return {
            ...item,
            submenuItems: filteredSubmenuItems,
          }
        }

        // Owner-only leaf (Activity Log) — hidden even from admins.
        if (item.ownerOnly) {
          return isOwner ? item : null
        }

        // Super-admin-only leaf (e.g. Campus Management) — hide from everyone else.
        if (item.superAdminOnly) {
          return isSuperAdmin ? item : null
        }

        // For leaf nodes (items without submenuItems), check requiredPermission
        if (item.requiredPermission) {
          // A teacher carries zero RBAC permission rows by design (they're
          // scoped by TeacherAssignment ownership on the backend instead),
          // which used to trip the "no permissions array -> show everything"
          // fail-open below meant for admin/owner roles -- leaking the full
          // admin menu (People, Fees, Reports, Admissions, Academic, Accounts,
          // User Management, Library...) into every teacher's sidebar.
          // Reported 2026-10-30. A teacher now only sees items explicitly
          // marked teacherAllowed; everyone else keeps the old behavior.
          const hasAccess = isTeacherRole
            ? !!item.teacherAllowed
            : !permissions?.length || permissions.includes(item.requiredPermission)

          if (hasAccess && (item.visible || process.env.REACT_APP_SKIP_VISIBLE_FLAG)) {
            return item
          }
          return null
        }

        // If no requiredPermission is specified, keep the item
        return item
      })
      .filter((item: any) => item !== null) // Remove null items
  }

  return filterItems(SidebarData)
}

// Usage example:
// const filteredSidebar = filterSidebarByPermissions(permissionsArray);
export { appendQueryParams }
